/**
 * Safe HTML rendering utilities
 * Prevents XSS attacks while allowing safe HTML rendering
 */

import React from 'react';
import sanitizeHtml from './sanitizeHtml';

/**
 * Safe HTML content component
 * Only renders plain text to prevent XSS
 */
export const SafeHTML: React.FC<{ content?: string | null }> = ({ content }) => {
  if (!content) return null;
  return <>{content}</>;
};

/**
 * Safely renders HTML-like content as plain text with line breaks
 * Strips HTML tags and renders safely
 */
export const SafeTextWithLineBreaks: React.FC<{ text?: string | null }> = ({ text }) => {
  if (!text) return null;

  // Strip HTML tags using DOMPurify for safety
  const plainText = sanitizeHtml(text).replace(/<[^>]*>/g, '');

  // Split by line breaks and render
  const lines = plainText.split(/\n|\r\n/);
  return (
    <>
      {lines.map((line, index) => (
        <div key={index}>{line}</div>
      ))}
    </>
  );
};

/**
 * Safe HTML component that renders sanitized HTML
 * Uses DOMPurify to sanitize content before rendering
 */
export const SafeHTMLContent: React.FC<{ html?: string | null }> = ({ html }) => {
  if (!html) return null;
  
  const sanitizedHtml = sanitizeHtml(html);
  return <div dangerouslySetInnerHTML={{ __html: sanitizedHtml }} />;
};

/**
 * Export the sanitizeHtml function for compatibility
 * This now uses the DOMPurify-based implementation
 */
export { default as sanitizeHtml } from './sanitizeHtml';
